import React, { useEffect, useState } from "react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Lock, KeyRound } from "lucide-react";

function ResetPassword() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    newPassword: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  const isValidPassword = formData.newPassword.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    } else if (!isValidPassword) {
      newErrors.confirmPassword =
        "Confirm Password must be at least 6 characters";
    }

    if (
      formData.newPassword &&
      formData.confirmPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      newErrors.general = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (!token) {
      setMessage("Invalid reset link.");
      return;
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const res = await userAPI.resetPassword(token, formData);
      if (res.code === 200) {
        showSuccess(res.message);
        navigate("/users/login");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <div className="custom-register-wrapper">
      <Container fluid className="p-0">
        <Row className="custom-register-row g-0">
          {/* Left Side: Visual Panel */}
         <Col md={6} lg={7} className="d-none d-md-block">
            <div className="custom-register-visual">
              <div className="custom-register-visual-content">
                <span className="visual-tag">Ivoréa</span>
                <h3 className="visual-heading">
                  Join the <span>Collection</span>
                </h3>
                <p className="visual-desc">
                  Create an account to unlock personalized recommendations,
                  faster checkout, and early access to new arrivals.
                </p>
              </div>
            </div>
          </Col>

          {/* Right Side: Reset Password Form */}
          <Col
            lg={6}
            className="d-flex justify-content-center align-items-center"
          >
            <div className="custom-register-container">
              <h1 className="custom-register-heading mb-1">
                Reset Password
              </h1>
              <p className="custom-register-subheading mb-4">
                Enter details to reset your password
              </p>

              <Form method="post" onSubmit={handleSubmit}>
                <div className="custom-register-field mb-3">
                  <Form.Label className="custom-register-label">
                    New Password <span className="required-star">*</span>
                  </Form.Label>
                  <div className="custom-register-input-box">
                    <Lock size={18} className="custom-register-icon" />
                    <Form.Control
                      type="password"
                      name="newPassword"
                      placeholder="New Password"
                      className="custom-register-input"
                      value={formData.newPassword}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.newPassword && (
                    <span className="custom-input-error">
                      {errors.newPassword}
                    </span>
                  )}
                </div>

                <div className="custom-register-field mb-3">
                  <Form.Label className="custom-register-label">
                    Confirm Password <span className="required-star">*</span>
                  </Form.Label>
                  <div className="custom-register-input-box">
                    <KeyRound size={18} className="custom-register-icon" />
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      className="custom-register-input"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.confirmPassword && (
                    <span className="custom-input-error">
                      {errors.confirmPassword}
                    </span>
                  )}
                </div>

                {errors.general && (
                  <span className="custom-input-error d-block mb-2">
                    {errors.general}
                  </span>
                )}
                {message && (
                  <span className="custom-input-error d-block mb-2">
                    {message}
                  </span>
                )}

                <Button
                  className="custom-register-submit w-100 mt-2"
                  type="submit"
                >
                  Reset Password
                </Button>

                <p className="footer-muted-text text-center mt-4 mb-0">
                  Remembered your password?{" "}
                  <Link to="/users/login" className="custom-register-link-main">
                    Back to Login
                  </Link>
                </p>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ResetPassword;